<template>
  <div>
    <send-exposure-chart-form
      :clientId="clientId"
      v-model="sendChartFormVisible"
      @input="sendChartFormVisible = $event"
    />

    <page-toolbar>
      <kendo-button
        type="button"
        @click="onExport(lastParams)"
        icon="excel"
        title="Export to Excel"
      />
      <kendo-button
        type="button"
        @click="showChart(lastParams)"
        title="Open Chart"
        style="display:inline-block"
      >
        <v-icon small>bar_chart</v-icon>
      </kendo-button>
      <kendo-button
        type="button"
        :class="{'k-force-state-active': drilldownOpen}"
        @click="drillDown(lastParams)"
        icon="list-bulleted"
        title="Transaction open for delivery"
        v-show="getCcyPairIsFx"
      />
      <kendo-button
        type="button"
        style="display:inline-block"
        icon="copy"
        title="Copy report to clipboard."
        @click="copyReportToClipboard"
      />
    </page-toolbar>
    <page-toolbar>
      <exposure-toolbar
        @change="onChange"
        @export="onExport"
        showQuarter
        :show-option-drop-down="true"
        :show-export-to-excel="false"
        v-slot="slotProps"
        :marketId="marketId"
      >
      </exposure-toolbar>
    </page-toolbar>

    <div id="exposureReportContainer" v-show="!drilldownOpen">
      <div ref="container" class="exposureGrid"></div>
    </div>

    <exposure-drilldown
      :reportParameters="lastParams"
      :is-read-only="isReadOnly"
      :value="drilldownOpen"
      component="div"
      @input="drilldownOpen = $event"
    />

    <simple-dialog
      v-model="chartDialog"
      :isDialog="false"
      :width="830"
      :height="420"
      title="Exposure chart"
    >
      <kendo-button
        v-if="!chartLoading"
        style="z-index:1;position:absolute;top:8px"
        icon="window"
        title="Send Chart"
        @click="openSendChartForm"
      />
      <kendo-button
        v-if="!chartLoading"
        style="z-index:1;position:absolute;top:8px;left:50px"
        icon="copy"
        title="Copy chart to clipboard."
        @click="copyChartToClipboard"
      />
      <div id="exposureChartContainer" class="chart" ref="marketDataChart" />
      <v-progress-linear :indeterminate="true" v-if="chartLoading"></v-progress-linear>
    </simple-dialog>
  </div>
</template>

<script>
import PageToolbar from '@/components/PageToolbar'
import ExposureToolbar from '../../components/Report/ExposureToolbar2'
import ExposureDrilldown from '../../components/Report/ExposureDrilldown'
import exposureGrid from '../../dashboard-components/exposure-grid'
import exposureChart from '../../dashboard-components/exposure-chart'
import params from '../../helpers/params'
import {getDataItem} from '../../helpers/kendo-helpers'
import {mapGetters} from 'vuex'
import security from '@/mixins/security'
import SimpleDialog from '@/components/SimpleDialog'
import SendExposureChartForm from '@/components/SendExposureChartForm'
import notification from '@/helpers/notification'

export default {
  mixins: [security],
  data() {
    return {
      chartDialog: false,
      chartLoading: false,
      lastParams: null,
      drilldownOpen: false,
      sendChartFormVisible: false,
    }
  },
  mounted() {
    this.$el.addEventListener('click', this.drilldownClick, false)
  },
  methods: {
    drilldownClick({target}) {
      if (!/adjustmentDrillDown|hedge\w+DrillDown/.test(target.className)) return

      const dataItem = getDataItem(target)

      const startDate = kendo.toString(dataItem.PeriodStart, 's')
      const endDate = kendo.toString(dataItem.PeriodEnd, 's')
      const {className} = target
      const columnType = className.includes('adjustmentDrillDown')
        ? 3
        : className.includes('hedgeCDrillDown')
        ? 1
        : 2

      const drilldownParams = {startDate, endDate, columnType}
      this.lastParams = {...this.lastParams, ...drilldownParams}
      this.drilldownOpen = true
    },
    drillDown(e) {
      if (this.drilldownOpen) {
        this.drilldownOpen = false
        return
      }
      const columnType = 4
      const startDate = e.asOfDate
      const endDate = e.toDate
      const drilldownParams = {startDate, endDate, columnType}
      this.lastParams = {...this.lastParams, ...drilldownParams}
      this.drilldownOpen = true
    },
    onChange(e) {
      this.drilldownOpen = false
      exposureGrid.render(kendo.jQuery(this.$refs.container), e, 'report')
      this.lastParams = e
    },
    onExport(e) {
      const qs = params.fromObject({
        t: this.tokenString,
        ...e,
      })
      location.href = `/api/report/ExposureReport/Export?${qs}`
    },
    async showChart(params) {
      kendo.destroy(this.$refs.marketDataChart)
      kendo.jQuery(this.$refs.marketDataChart).empty()
      this.chartDialog = true
      this.chartLoading = true
      await exposureChart.render(this.$refs.marketDataChart, params, 'report').catch(() => {
        this.chartLoading = false
      })
      this.chartLoading = false
    },
    openSendChartForm() {
      this.sendChartFormVisible = true
    },
    async copyChartToClipboard() {
      const exposureChart = await this.getExposureChart()
      await navigator.clipboard.write([new window.ClipboardItem({'image/png': exposureChart})])
      notification('Chart copied to clipboard.', 'success')
    },
    async getExposureChart() {
      const root = await kendo.drawing.drawDOM(kendo.jQuery('#exposureChartContainer'))
      const data = await kendo.drawing.exportImage(root)
      return this.dataURItoBlob(data)
    },
    async copyReportToClipboard() {
      const exposureReport = await this.getExposureReport()
      await navigator.clipboard.write([new window.ClipboardItem({'image/png': exposureReport})])
      notification('Report copied to clipboard.', 'success')
    },
    async getExposureReport() {
      const root = await kendo.drawing.drawDOM(kendo.jQuery('#exposureReportContainer'))
      const data = await kendo.drawing.exportImage(root)
      return this.dataURItoBlob(data)
    },
    dataURItoBlob(dataURI) {
      const binary = atob(dataURI.split(',')[1])
      const array = []
      for (var i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i))
      }

      return new Blob([new Uint8Array(array)], {type: 'image/png'})
    },
  },
  computed: {
    ...mapGetters({tokenString: 'authentication/tokenString'}),
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    ...mapGetters({marketId: 'app/marketId'}),
    getCcyPairIsFx() {
      return this.lastParams != null && this.lastParams.foreignCcyIsFx
    },
  },
  beforeDestroy() {
    kendo.destroy(this.$refs.container)
    kendo.destroy(this.$refs.marketDataChart)
    this.$el.removeEventListener('click', this.drilldownClick)
  },
  components: {
    PageToolbar,
    ExposureToolbar,
    ExposureDrilldown,
    SimpleDialog,
    SendExposureChartForm,
  },
}
</script>

<style scoped>
.exposureGrid {
  font-size: 0.9em;
}
.chart {
  overflow: hidden;
}
button.k-button {
  margin-left: 8px;
}
</style>
<style>
.hedgeCDrillDown,
.hedgeNCDrillDown,
.adjustmentDrillDown {
  text-decoration: underline;
}

.hedgeCDrillDown:hover,
.hedgeNCDrillDown:hover,
.adjustmentDrillDown:hover {
  cursor: pointer;
}
.k-force-state-active {
  border-color: rgba(0, 0, 0, 0.08) !important;
  color: #ffffff !important;
  background-color: #1e355e !important;
}
</style>
